import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import { supportSettingsService } from '../../services/supportSettingsService';
import { SupportSettings } from '../../types/supportSettings';
import {
  DEFAULT_HELPDESK_HOME_SCREEN,
  HELPDESK_HOME_SCREEN_OPTIONS,
  HelpdeskHomeScreen,
  normalizeHelpdeskHomeScreen,
} from '../../utils/helpdeskHomeScreen';
import { cacheAdminHelpdeskHomeScreen } from '../../utils/helpdeskHomePreferenceStorage';
import { useAuth } from '../../contexts/AuthContext';

export const SupportSettingsPage: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SupportSettings | null>(null);
  const [form, setForm] = useState({
    btalkAutoSendOnFinalize: false,
    autoResolveEnabled: false,
    autoResolveInactivityMinutes: 60,
    botShareHumanHistoryWithAgent: true,
    defaultHomeScreen: DEFAULT_HELPDESK_HOME_SCREEN as HelpdeskHomeScreen,
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try
    {
      const data = await supportSettingsService.get();
      const defaultHomeScreen = normalizeHelpdeskHomeScreen(data.defaultHomeScreen);
      cacheAdminHelpdeskHomeScreen(defaultHomeScreen);
      setSettings(data);
      setForm({
        btalkAutoSendOnFinalize: data.btalkAutoSendOnFinalize,
        autoResolveEnabled: data.autoResolveEnabled,
        autoResolveInactivityMinutes: data.autoResolveInactivityMinutes ?? 60,
        botShareHumanHistoryWithAgent: data.botShareHumanHistoryWithAgent,
        defaultHomeScreen,
      });
    }
    catch
    {
      addToast('Erro ao carregar configurações', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (form.autoResolveEnabled && form.autoResolveInactivityMinutes <= 0)
    {
      addToast('Informe um tempo de inatividade válido', 'error');
      return;
    }

    setSaving(true);
    try
    {
      const updated = await supportSettingsService.update({
        btalkAutoSendOnFinalize: form.btalkAutoSendOnFinalize,
        autoResolveEnabled: form.autoResolveEnabled,
        autoResolveInactivityMinutes: form.autoResolveEnabled ? form.autoResolveInactivityMinutes : null,
        botShareHumanHistoryWithAgent: form.botShareHumanHistoryWithAgent,
        defaultHomeScreen: form.defaultHomeScreen,
        configuration: settings?.configuration,
      });
      cacheAdminHelpdeskHomeScreen(updated.defaultHomeScreen);
      setSettings(updated);

      addToast('Configurações salvas com sucesso', 'success');
    }
    catch
    {
      addToast('Erro ao salvar configurações', 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Carregando configurações...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Configurações do Atendimento</h1>
        <p className="text-gray-600 mt-1">
          Parâmetros globais do módulo para integração com bTalk, auto-resolução e agentes de IA.
        </p>
      </div>

      <div className="space-y-6">
        {(user?.isAdmin || (user?.role?.code ?? '').toLowerCase() === 'admin') && (
          <section className="bg-white border border-gray-200 rounded-lg shadow p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Tela inicial padrão da organização</h2>
                <p className="text-sm text-gray-600">
                  Define a tela aberta ao entrar no módulo para todos os atendentes. Cada um pode personalizar acessando o menu (⋮) ao lado de "Voltar para Aplicação".
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {HELPDESK_HOME_SCREEN_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className={`flex items-start gap-3 cursor-pointer rounded-lg border-2 p-3 transition-all ${
                    form.defaultHomeScreen === option.value
                      ? 'border-blue-600 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="org-home"
                    className="sr-only"
                    checked={form.defaultHomeScreen === option.value}
                    onChange={() => setForm(f => ({ ...f, defaultHomeScreen: option.value }))}
                  />
                  <svg
                    className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                      form.defaultHomeScreen === option.value ? 'text-blue-600' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.icon} />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <span className="block font-medium text-gray-900">{option.label}</span>
                    <span className="block text-sm text-gray-600">{option.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </section>
        )}

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pipeline bTalk</h2>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900"
              checked={form.btalkAutoSendOnFinalize}
              onChange={e => setForm(f => ({ ...f, btalkAutoSendOnFinalize: e.target.checked }))}
            />
            <span>
              <span className="block font-medium text-gray-900">Enviar automaticamente ao finalizar</span>
              <span className="block text-sm text-gray-500">
                Quando desativado, o envio ao bTalk poderá ser feito manualmente na conversa finalizada.
              </span>
            </span>
          </label>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Auto-resolução</h2>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900"
                checked={form.autoResolveEnabled}
                onChange={e => setForm(f => ({ ...f, autoResolveEnabled: e.target.checked }))}
              />
              <span>
                <span className="block font-medium text-gray-900">Habilitar auto-resolução</span>
                <span className="block text-sm text-gray-500">
                  Finaliza conversas automaticamente conforme os critérios abaixo.
                </span>
              </span>
            </label>

            {form.autoResolveEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inatividade do contato (minutos)
                </label>
                <input
                  type="number"
                  min={1}
                  className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-900 focus:border-blue-900"
                  value={form.autoResolveInactivityMinutes}
                  onChange={e => setForm(f => ({
                    ...f,
                    autoResolveInactivityMinutes: parseInt(e.target.value, 10) || 0,
                  }))}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Conversas abertas sem mensagem do contato nesse período são finalizadas.
                  Mensagens do bot ou do operador não reiniciam o prazo.
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Agentes de IA</h2>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900"
              checked={form.botShareHumanHistoryWithAgent}
              onChange={e => setForm(f => ({ ...f, botShareHumanHistoryWithAgent: e.target.checked }))}
            />
            <span>
              <span className="block font-medium text-gray-900">Compartilhar histórico humano com o bot</span>
              <span className="block text-sm text-gray-500">
                Permite que o agente de IA acesse mensagens trocadas com operadores durante handoff.
              </span>
            </span>
          </label>
        </section>

        <div className="flex justify-end">
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar configurações'}
          </Button>
        </div>
      </div>
    </div>
  );
};
